import exifr from 'exifr';
import { RawExifMetadata, NormalizedExifData } from '../types/exif';

export const EXIF_PICK_TAGS = [
  'DateTimeOriginal',
  'CreateDate',
  'ModifyDate',
  'SubSecTimeOriginal',
  'OffsetTimeOriginal',
  'OffsetTime',
  'OffsetTimeDigitized',
  'Orientation',
  'Make',
  'Model',
  'LensModel',
  'FocalLength',
  'FNumber',
  'ExposureTime',
  'ISO',
  'latitude',
  'longitude',
  'GPSDateStamp',
  'GPSTimeStamp',
];

/**
 * Normalizes an EXIF date string ("YYYY:MM:DD HH:MM:SS" or "YYYY-MM-DD...") to ISO-like "YYYY-MM-DD HH:MM:SS".
 */
export function normalizeExifDateString(rawDate?: string): string | null {
  if (!rawDate || typeof rawDate !== 'string') return null;
  const trimmed = rawDate.trim();
  // Filter corrupted EXIF strings (e.g. "0000:00:00 00:00:00")
  if (trimmed.startsWith('0000') || trimmed.length < 19) return null;

  const match = /^(\d{4})[:\-](\d{2})[:\-](\d{2})\s+(\d{2}):(\d{2}):(\d{2})/.exec(trimmed);
  if (!match) return null;

  const [, Y, M, D, h, m, s] = match;
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
}

/**
 * Formats numeric shutter speed to traditional photography fraction string.
 * e.g. 0.002 -> "1/500s", 0.5 -> "1/2s", 2.0 -> "2s"
 */
export function formatShutterSpeed(seconds?: number): string | undefined {
  if (!Number.isFinite(seconds) || seconds === undefined || seconds === null || seconds <= 0) return undefined;
  if (seconds >= 1) {
    return `${Number.isInteger(seconds) ? seconds : seconds.toFixed(1)}s`;
  }
  const denominator = Math.round(1 / seconds);
  return `1/${denominator}s`;
}

/**
 * Extracts and normalizes EXIF metadata from a file or blob via chunked header parsing.
 */
export async function extractExifMetadata(file: File | Blob): Promise<NormalizedExifData> {
  let raw: RawExifMetadata | undefined;

  try {
    raw = await exifr.parse(file, {
      tiff: true,
      exif: true,
      gps: true,
      reviveValues: false, // Critical: preserve exact wall-clock strings
      pick: EXIF_PICK_TAGS,
    });
  } catch {
    // Corrupted file headers or format unsupported by exifr
    raw = undefined;
  }

  // 1. Resolve date string
  let dateTimeStr =
    normalizeExifDateString(raw?.DateTimeOriginal) ||
    normalizeExifDateString(raw?.CreateDate) ||
    normalizeExifDateString(raw?.ModifyDate);

  let isDateFallback = false;

  if (!dateTimeStr) {
    isDateFallback = true;
    if (file instanceof File && file.lastModified > 0) {
      const d = new Date(file.lastModified);
      const Y = d.getFullYear();
      const M = String(d.getMonth() + 1).padStart(2, '0');
      const D = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      dateTimeStr = `${Y}-${M}-${D} ${h}:${m}:${s}`;
    }
  }

  // 2. Subsecond parsing
  let subseconds = 0;
  if (raw?.SubSecTimeOriginal) {
    const rawSub = String(raw.SubSecTimeOriginal).trim();
    const parsedSub = parseFloat(`0.${rawSub}`);
    if (!isNaN(parsedSub)) subseconds = parsedSub;
  }

  // 3. Timezone offset string (EXIF 2.31 tag)
  const offsetTime =
    raw?.OffsetTimeOriginal?.trim() ||
    raw?.OffsetTime?.trim() ||
    raw?.OffsetTimeDigitized?.trim() ||
    null;

  // 4. GPS extraction
  const hasExifGps = typeof raw?.latitude === 'number' && typeof raw?.longitude === 'number';
  const gpsCoordinates = hasExifGps
    ? { lat: raw!.latitude!, lon: raw!.longitude! }
    : undefined;

  // 5. GPS satellite UTC clock resolution (for Tier 2 auto-calibration)
  let gpsSatelliteUtc: number | undefined;
  if (raw?.GPSDateStamp && raw?.GPSTimeStamp) {
    try {
      const dateParts = String(raw.GPSDateStamp).split(':');
      let h = 0,
        m = 0,
        s = 0;
      if (Array.isArray(raw.GPSTimeStamp)) {
        h = raw.GPSTimeStamp[0] || 0;
        m = raw.GPSTimeStamp[1] || 0;
        s = raw.GPSTimeStamp[2] || 0;
      } else if (typeof raw.GPSTimeStamp === 'string') {
        const timeParts = raw.GPSTimeStamp.split(':');
        h = parseInt(timeParts[0], 10) || 0;
        m = parseInt(timeParts[1], 10) || 0;
        s = parseFloat(timeParts[2]) || 0;
      }
      if (dateParts.length === 3) {
        const Y = parseInt(dateParts[0], 10);
        const M = parseInt(dateParts[1], 10) - 1;
        const D = parseInt(dateParts[2], 10);
        gpsSatelliteUtc = Math.floor(Date.UTC(Y, M, D, h, m, Math.floor(s)) / 1000) + (s % 1);
      }
    } catch {
      gpsSatelliteUtc = undefined;
    }
  }

  // 6. Camera specifications formatting
  const camera = {
    make: raw?.Make?.trim(),
    model: raw?.Model?.trim(),
    lens: raw?.LensModel?.trim(),
    focalLength: raw?.FocalLength ? `${Math.round(raw.FocalLength)}mm` : undefined,
    aperture: raw?.FNumber ? `f/${raw.FNumber.toFixed(1)}` : undefined,
    shutterSpeed: formatShutterSpeed(raw?.ExposureTime),
    iso: raw?.ISO,
  };

  return {
    dateTimeOriginal: dateTimeStr,
    subseconds,
    offsetTime,
    orientation: raw?.Orientation || 1,
    hasExifGps,
    gpsCoordinates,
    gpsSatelliteUtc,
    camera,
    isDateFallback,
  };
}
